export interface Patient {
  full_name: string;
  care_of: string;
  id_mark: string;
  full_address: string;
  organisation: string;
  caretaker_full_name: string;
  caretaker_name: any;
  caretaker_last_name: any;
  caretaker_age: string;
  PK: string;
  SK: string;
  id: string;
  doc_id: string;
  name: string;
  last_name: string;
  age?: string;
  gender?: string;
  certificate_type: string;
  status: 'pending'|'assigned'| 'rejected' | 'issued' | 'submitted' | 'refunded';
  diagnosis?: string;
  no_of_days?: string;
  from_date?: string;
  city?: string;
  state?: string;
  created_at: string;
  updated_at: string;
  assigned_doctor_id?: string;
  assigned_doctor_name?: string;
  additional_details?: string;
  detail_of_illness?: string;
}

// src/types/index.ts (or wherever)

export interface Doctor {
  PK: string;
  SK: string;
  id: string;
  name: string;
  regNo: string;
  specialization: string;
  createdAt: string;
  updatedAt: string;
  signatureKey?: string;
  signatureUrl?: string;
}

export interface Template {
  PK: string;
  SK: string;
  template_id: string;
  name: string;
  description?: string;
  category: string;
  template_data: any;
  fields: string[];
  created_at: string;
  updated_at: string;
}

// src/types/index.ts

/**
 * Central export file for all TypeScript types
 * Import from here: import { PrescriptionFormData, PDFTemplateData } from '@/types';
 */

// ============================================================================
// PRESCRIPTION TYPES
// ============================================================================

/**
 * Main form data interface for prescription form
 */
export interface PrescriptionFormData {
  // PDF Template Direct Fields
  drName: string;
  specialization: string;
  regNo: string;
  patientName: string;
  isuueDate: string; // Note: keeping typo to match your template field name
  docId: string;
  ageGender: string; // Auto-generated from age + gender
  
  // Content Box Components (will be formatted into contentBox field)
  complaintsOf: string;
  historyOfSymptoms: string;
  pastHistoryOfSimilarSymptoms: 'Yes' | 'No';
  concurrentIllnesses: string;
  substanceIntake: 'Yes' | 'No';
  substanceIntakeDetails?: string;
  significantPastHistory: string;
  historyOfSurgery: 'Yes' | 'No';
  surgeryDetails?: string;
  historyOfTravel: 'Yes' | 'No';
  travelDetails?: string;
  relevantFamilyHistory: 'Yes' | 'No';
  familyHistoryDetails?: string;
  intensityOfSymptoms: 'Mild' | 'Moderate' | 'Severe';
  selfMedicationHomeRemedies: 'Yes' | 'No';
  selfMedicationDetails?: string;
  specialNotesByRMP: string;
  adviceByRMP: string;
  fullDiagnosis: string;
  rxLines: string[];
}

/**
 * Data format that matches your PDF template exactly
 */
export interface PDFTemplateData {
  drName: string;
  specialization: string;
  regNo: string;
  patientName: string;
  isuueDate: string;
  docId: string;
  ageGender: string;
  contentBox: string; // Formatted text with all prescription content
}

/**
 * Gender options
 */
export type Gender = 'Male' | 'Female' | 'Other';

/**
 * Yes/No answer type
 */
export type YesNo = 'Yes' | 'No';

/**
 * Symptom intensity levels
 */
export type SymptomIntensity = 'Mild' | 'Moderate' | 'Severe';

// ============================================================================
// API TYPES
// ============================================================================

/**
 * Generic API Response wrapper
 */
export interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * API Response from getTemplate
 */
export interface TemplateResponse {
  success: boolean;
  data?: {
    tempId: string;
    name: string;
    description: string;
    category: string;
    fieldSchema: string[];
    templateSchema: {
      basePdf: string;
      schemas: any[][];
      pdfmeVersion?: string;
    };
    isActive: boolean;
    isPublic: boolean;
    createdAt: string;
    updatedAt: string;
    usageCount: number;
  };
  error?: string;
}

/**
 * Template List Item
 */
export interface TemplateListItem {
  tempId: string;
  name: string;
  description?: string;
  category?: string;
  fieldSchema: string[];
  createdAt: string;
  updatedAt?: string;
  usageCount?: number;
}

/**
 * Create Template Request
 */
export interface CreateTemplateRequest {
  tempId: string;
  name: string;
  description: string;
  category: string;
  templateSchema: any;
  fieldSchema: string[];
}

/**
 * Update Template Request
 */
export interface UpdateTemplateRequest {
  tempId: string;
  name?: string;
  description?: string;
  templateSchema?: any;
  fieldSchema?: string[];
}

/**
 * Get Template Request
 */
export interface GetTemplateRequest {
  tempId: string;
}

/**
 * Check Template Exists Request
 */
export interface CheckTemplateExistsRequest {
  tempId: string;
}

/**
 * List Templates Request
 */
export interface ListTemplatesRequest {
  category?: string;
  limit?: number;
  offset?: number;
}

/**
 * Generate PDF Request
 */
export interface GeneratePDFRequest {
  tempId: string;
  data: Record<string, any>;
}

/**
 * Generate PDF Response
 */
export interface GeneratePDFResponse {
  success: boolean;
  pdfUrl?: string;
  pdfBuffer?: ArrayBuffer;
  error?: string;
}

// ============================================================================
// PDFME TEMPLATE TYPES
// ============================================================================

/**
 * PDFME Template Schema
 */
export interface PDFMETemplate {
  basePdf: string | ArrayBuffer;
  schemas: PDFMESchema[][];
  pdfmeVersion?: string;
}

/**
 * PDFME Schema Field
 */
export interface PDFMESchema {
  name: string;
  type: 'text' | 'image' | 'qrcode' | 'barcode';
  position: Position;
  width: number;
  height: number;
  alignment?: 'left' | 'center' | 'right';
  verticalAlignment?: 'top' | 'middle' | 'bottom';
  fontSize?: number;
  fontName?: string;
  fontColor?: string;
  backgroundColor?: string;
  lineHeight?: number;
  characterSpacing?: number;
  opacity?: number;
  rotate?: number;
  required?: boolean;
  readOnly?: boolean;
  content?: string;
  underline?: boolean;
  strikethrough?: boolean;
}

/**
 * Position coordinates
 */
export interface Position {
  x: number;
  y: number;
}

// ============================================================================
// DYNAMODB TYPES
// ============================================================================

/**
 * DynamoDB Template Structure
 */
export interface DynamoDBTemplate {
  tempId: {
    S: string;
  };
  Id: {
    N: string;
  };
  name: {
    S: string;
  };
  description: {
    S: string;
  };
  category: {
    S: string;
  };
  fieldSchema: {
    L: Array<{ S: string }>;
  };
  templateSchema: {
    M: {
      basePdf: { S: string };
      pdfmeVersion: { S: string };
      schemas: {
        L: Array<{
          L: Array<{ M: any }>;
        }>;
      };
    };
  };
  userId: {
    S: string;
  };
  isActive: {
    BOOL: boolean;
  };
  isPublic: {
    BOOL: boolean;
  };
  createdAt: {
    S: string;
  };
  updatedAt: {
    S: string;
  };
  lastUsedAt: {
    S: string;
  };
  usageCount: {
    N: string;
  };
  version: {
    N: string;
  };
}

// ============================================================================
// MEDICATION TYPES
// ============================================================================

/**
 * Common medication preset
 */
export interface MedicationPreset {
  name: string;
  fullText: string;
  category?: 'common' | 'diabetes' | 'cardiac' | 'gastric' | 'psychiatric';
}

/**
 * Medication timing abbreviations
 */
export type MedicationTiming = 
  | 'OD'     // Once daily
  | 'BD'     // Twice daily
  | 'TDS'    // Three times daily
  | 'QID'    // Four times daily
  | 'HS'     // At bedtime
  | 'AC'     // Before meals
  | 'PC'     // After meals
  | 'SOS'    // If needed
  | 'STAT';  // Immediately

/**
 * Medication frequency details
 */
export interface MedicationFrequency {
  code: MedicationTiming;
  description: string;
  timesPerDay?: number;
}

// ============================================================================
// FORM TYPES
// ============================================================================

/**
 * Form field configuration
 */
export interface FormFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'date' | 'number';
  required?: boolean;
  placeholder?: string;
  options?: SelectOption[];
  gridColumn?: string;
  rows?: number;
  maxLength?: number;
  pattern?: string;
  hint?: string;
}

/**
 * Select dropdown option
 */
export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Form section grouping
 */
export interface FormSection {
  id: string;
  title: string;
  icon?: string;
  fields: FormFieldConfig[];
}

/**
 * Form mode
 */
export type FormMode = 'create' | 'edit' | 'view';

/**
 * Form component props
 */
export interface FormProps {
  mode?: FormMode;
  initialData?: Partial<PrescriptionFormData>;
  onSubmit?: (data: PrescriptionFormData) => void | Promise<void>;
  onCancel?: () => void;
}

/**
 * Form validation error
 */
export interface FormValidationError {
  field: keyof PrescriptionFormData;
  message: string;
}

/**
 * Form validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: FormValidationError[];
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Make all properties optional recursively
 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Make specific properties required
 */
export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

/**
 * Omit multiple properties
 */
export type OmitMultiple<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

/**
 * Extract values from object type
 */
export type ValueOf<T> = T[keyof T];

// ============================================================================
// COMPONENT PROPS TYPES
// ============================================================================

/**
 * Loading state
 */
export interface LoadingState {
  isLoading: boolean;
  message?: string;
}

/**
 * Error state
 */
export interface ErrorState {
  hasError: boolean;
  message?: string;
  code?: string;
}

/**
 * Common component props
 */
export interface BaseComponentProps {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

// ============================================================================
// EXPORTS - All types are already exported above via interfaces/types
// ============================================================================

/**
 * Usage examples:
 * 
 * import { PrescriptionFormData, PDFTemplateData } from '@/types';
 * import type { Gender, YesNo, SymptomIntensity } from '@/types';
 * import type { APIResponse, TemplateResponse } from '@/types';
 */

type role = "admin" | "doctor" |"user";

export interface User {     
  id: string;
  email: string;
  role: role;
}

// features/cases/types/case.types.ts
export type CaseStatus = "pending" | "assigned" | "in_review" | "completed";

export interface Case {
  id: string;
  patientId: string;
  assignedDoctorId: string | null;
  status: CaseStatus;
  accessToken: string | null;
  createdAt: Date;
}

// src/components/types/FormTypes.ts

export const CERTIFICATE_TYPES = [
  'FITNESS CERTIFICATE',
  'MEDICAL CERTIFICATE',
  'SICK LEAVE CERTIFICATE',
  'RECOVERY CERTIFICATE',
  'CARETAKER CERTIFICATE',
  'UNFIT TO TRAVEL CERTIFICATE',
  'WORK FROM HOME CERTIFICATE',
] as const;

export interface CertificateFormData {
  patientName: string;
  drName: string;
  specialization: string;
  regNo: string;
  issueDate: string;
  docId: string;
  certificateType: string;
  to: string;
  organisation: string;
  signature: string;
  guardianName: string;
  age: string;
  gender: 'male' | 'female' | 'other' | '';
  identificationMark: string;
  address: string;
  examTime: string;
  examDate: string;
  diagnosis: string;
  noOfDays: string;
  fromDate: string;
  caretakerFullName: string;
  caretakerAge: string;
  caretakerRelation: string;
  fitStamp: string;
  content: string;
  validity: string;
  illnessDetails: string;
  additionalDetails: string;
  he_she: string;
  his_her: string;
  him_her: string;
  patientTitle: string;
}

export interface CognitoUser {
  username: string;
  email: string;
  name: string;
  role?: string;
  groups?: string[];
  email_verified: boolean;
  sub: string;
}

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface AuthState {
  user: CognitoUser | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupData {
  email: string;
  password: string;
  name: string;
  role?: string;
}

export interface PasswordResetData {
  email: string;
  code: string;
  newPassword: string;
}

export enum AuthProvider {
  COGNITO = 'COGNITO',
  GOOGLE = 'Google',
}

/* duplicate PrescriptionFormData definition removed; use the earlier
   interface (which has selfMedicationDetails?: string) */